import moment from 'moment-timezone';
import type {
  Candle,
  DetectedSetup,
  IndicatorSeries,
  RangeState,
  SessionKey,
  SetupDirection,
  SwingPoint,
  Timeframe,
  TrendState
} from './types.ts';

export type FvgType = 'bullish' | 'bearish';
export type FvgStrength = 'weak' | 'valid' | 'strong';

export type FvgZone = {
  type: FvgType;
  low: number;
  high: number;
  mid: number;
  tf: Timeframe;
  createdAt: number;
  originIndex: number;
  strength: FvgStrength;
  qualifiers: string[];
  expiresAfterBars: number;
};

type FvgDetectionOptions = {
  displacementMult?: number;
  lookback?: number;
  expiresAfterBars?: number;
  timeframe: Timeframe;
};

type FvgContext = {
  session: SessionKey;
  sessionPhase: 'early' | 'mid' | 'late';
  trend: TrendState;
  emaSlope: number | null;
  volatilityState: 'Expanding' | 'Neutral' | 'Low';
  atr: number | null;
  ema9: number | null;
  ema20: number | null;
  vwap: number | null;
  timeMs: number;
};

const TIME_ZONE = 'America/New_York';

const avg = (values: number[]) => values.reduce((sum, val) => sum + val, 0) / Math.max(values.length, 1);

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const buildContextAtIndex = (
  candles: Candle[],
  indicators: IndicatorSeries,
  trend: TrendState,
  session: SessionKey,
  sessionStart: string | undefined,
  sessionEnd: string | undefined,
  index: number
): FvgContext => {
  const timeMs = candles[index]?.t ?? candles[candles.length - 1]?.t ?? Date.now();
  const ema20 = indicators.ema20[index] ?? null;
  const ema20Prev = indicators.ema20[Math.max(index - 5, 0)] ?? null;
  const emaSlope = ema20 && ema20Prev ? ema20 - ema20Prev : null;

  const atr = indicators.atr14[index] ?? null;
  const atrWindow = indicators.atr14
    .slice(Math.max(index - 10, 0), index + 1)
    .filter((val): val is number => typeof val === 'number');
  const atrAvg = atrWindow.length ? avg(atrWindow) : null;
  let volatilityState: FvgContext['volatilityState'] = 'Neutral';
  if (atr && atrAvg) {
    if (atr < atrAvg * 0.85) volatilityState = 'Low';
    if (atr > atrAvg * 1.15) volatilityState = 'Expanding';
  }

  let sessionPhase: FvgContext['sessionPhase'] = 'mid';
  const bounds = sessionStart && sessionEnd
    ? {
        start: moment.tz(`${moment.tz(timeMs, TIME_ZONE).format('YYYY-MM-DD')} ${sessionStart}`, 'YYYY-MM-DD HH:mm', TIME_ZONE),
        end: moment.tz(`${moment.tz(timeMs, TIME_ZONE).format('YYYY-MM-DD')} ${sessionEnd}`, 'YYYY-MM-DD HH:mm', TIME_ZONE)
      }
    : null;
  if (bounds) {
    const total = bounds.end.diff(bounds.start, 'minutes');
    const elapsed = moment.tz(timeMs, TIME_ZONE).diff(bounds.start, 'minutes');
    const pct = total > 0 ? clamp(elapsed / total, 0, 1) : 0.5;
    if (pct <= 0.33) sessionPhase = 'early';
    else if (pct >= 0.75) sessionPhase = 'late';
  }

  return {
    session,
    sessionPhase,
    trend,
    emaSlope,
    volatilityState,
    atr,
    ema9: indicators.ema9[index] ?? null,
    ema20,
    vwap: indicators.vwap[index] ?? null,
    timeMs
  };
};

export const detectFVGs = (
  candles: Candle[],
  options: FvgDetectionOptions
): FvgZone[] => {
  const lookback = options.lookback ?? 20;
  const displacementMult = options.displacementMult ?? 1.25;
  const expiresAfterBars = options.expiresAfterBars ?? 30;

  const ranges = candles.map((c) => c.h - c.l);
  const fvgs: FvgZone[] = [];
  for (let i = 2; i < candles.length; i += 1) {
    const c1 = candles[i - 2];
    const c2 = candles[i - 1];
    const c3 = candles[i];
    const range2 = c2.h - c2.l;
    const windowStart = Math.max(0, i - 1 - lookback + 1);
    const avgRange = avg(ranges.slice(windowStart, i));
    if (avgRange <= 0 || range2 < displacementMult * avgRange) {
      continue;
    }
    if (c1.h < c3.l) {
      const low = c1.h;
      const high = c3.l;
      fvgs.push({
        type: 'bullish',
        low,
        high,
        mid: (low + high) / 2,
        tf: options.timeframe,
        createdAt: c3.t,
        originIndex: i,
        strength: 'weak',
        qualifiers: [],
        expiresAfterBars
      });
    }
    if (c1.l > c3.h) {
      const low = c3.h;
      const high = c1.l;
      fvgs.push({
        type: 'bearish',
        low,
        high,
        mid: (low + high) / 2,
        tf: options.timeframe,
        createdAt: c3.t,
        originIndex: i,
        strength: 'weak',
        qualifiers: [],
        expiresAfterBars
      });
    }
  }
  return fvgs;
};

const overlapsZone = (aLow: number, aHigh: number, bLow: number, bHigh: number) =>
  Math.max(aLow, bLow) <= Math.min(aHigh, bHigh);

const isAsiaLowVol = (ctx: FvgContext) => {
  const hour = moment.utc(ctx.timeMs).hour();
  const isAsia = hour >= 21 || hour < 7;
  return ctx.session === 'FX_24H' && isAsia && ctx.volatilityState === 'Low';
};

const isLateNY = (ctx: FvgContext) => ctx.session === 'NEW_YORK' && ctx.sessionPhase === 'late';

const isFlatTrend = (ctx: FvgContext) => {
  if (!ctx.ema20 || ctx.emaSlope == null) return false;
  const normalized = Math.abs(ctx.emaSlope) / ctx.ema20;
  return normalized < 0.0005;
};

const findRecentSwing = (swings: SwingPoint[], type: 'HIGH' | 'LOW', beforeIndex: number) =>
  [...swings].reverse().find((s) => s.type === type && s.index < beforeIndex);

export const qualifyFVG = (params: {
  fvg: FvgZone;
  ctx: FvgContext;
  setup: DetectedSetup;
  candles: Candle[];
  indicators: IndicatorSeries;
  swings: SwingPoint[];
}): { fvg: FvgZone; eligible: boolean; modifiers: Array<{ label: string; delta: number }> } => {
  const { fvg, ctx, setup, candles, indicators, swings } = params;
  const qualifiers: string[] = [];
  const modifiers: Array<{ label: string; delta: number }> = [];

  if (isAsiaLowVol(ctx)) {
    modifiers.push({ label: 'Asia low volatility', delta: -10 });
    return {
      fvg: { ...fvg, strength: 'weak', qualifiers: [] },
      eligible: false,
      modifiers
    };
  }

  if (ctx.trend === 'RANGE' || isFlatTrend(ctx)) {
    modifiers.push({ label: 'Flat trend / range', delta: -5 });
    return {
      fvg: { ...fvg, strength: 'weak', qualifiers: [] },
      eligible: false,
      modifiers
    };
  }

  if (isLateNY(ctx)) {
    modifiers.push({ label: 'Late NY session', delta: -5 });
  }

  if (ctx.trend === 'UP' && fvg.type === 'bullish') qualifiers.push('HTF trend aligned');
  if (ctx.trend === 'DOWN' && fvg.type === 'bearish') qualifiers.push('HTF trend aligned');

  if (ctx.session === 'LONDON' || ctx.session === 'NEW_YORK' || ctx.volatilityState === 'Expanding') {
    qualifiers.push('Session expansion');
  }

  if (ctx.vwap && ctx.atr) {
    const dist = Math.abs(fvg.mid - ctx.vwap);
    if (dist <= ctx.atr * 0.5) {
      qualifiers.push('VWAP proximity');
    }
  }

  if (ctx.ema9 && ctx.ema20) {
    const emaLow = Math.min(ctx.ema9, ctx.ema20);
    const emaHigh = Math.max(ctx.ema9, ctx.ema20);
    if (overlapsZone(fvg.low, fvg.high, emaLow, emaHigh)) {
      qualifiers.push('EMA pullback overlap');
    }
  }

  const swingType = fvg.type === 'bullish' ? 'LOW' : 'HIGH';
  const recentSwing = findRecentSwing(swings, swingType, fvg.originIndex);
  if (recentSwing) {
    const sweepCandle = candles[fvg.originIndex];
    if (
      (fvg.type === 'bullish' && sweepCandle.l < recentSwing.price && sweepCandle.c > recentSwing.price) ||
      (fvg.type === 'bearish' && sweepCandle.h > recentSwing.price && sweepCandle.c < recentSwing.price)
    ) {
      qualifiers.push('Liquidity sweep reversal');
    }
  }

  const strength: FvgStrength = qualifiers.length >= 3 ? 'strong' : qualifiers.length >= 2 ? 'valid' : 'weak';
  return {
    fvg: {
      ...fvg,
      strength,
      qualifiers
    },
    eligible: strength !== 'weak',
    modifiers
  };
};

const directionMatches = (direction: SetupDirection, fvg: FvgZone) =>
  (direction === 'LONG' && fvg.type === 'bullish') || (direction === 'SHORT' && fvg.type === 'bearish');

export const isFvgExpired = (fvg: FvgZone, currentIndex: number) =>
  currentIndex - fvg.originIndex > fvg.expiresAfterBars;

const scoreModifierForStrength = (strength: FvgStrength) => {
  if (strength === 'strong') return { label: 'FVG Refined (strong)', delta: 12 };
  if (strength === 'valid') return { label: 'FVG Refined (valid)', delta: 8 };
  return { label: 'FVG present', delta: 0 };
};

const computeBuffer = (atr: number | null, price: number) => {
  if (atr) return atr * 0.1;
  return price * 0.001;
};

export const applyExecutionModels = (params: {
  setup: DetectedSetup;
  candles: Candle[];
  indicators: IndicatorSeries;
  trend: TrendState;
  range: RangeState | null;
  session: SessionKey;
  sessionStart?: string;
  sessionEnd?: string;
  fvgCandidates: FvgZone[];
  swings: SwingPoint[];
}): DetectedSetup => {
  const { setup, candles, indicators, trend, session, sessionStart, sessionEnd, fvgCandidates, swings } = params;
  const primarySetups = ['EMA_PULLBACK', 'RANGE_FADE', 'FAILED_BREAKOUT', 'ORB', 'EXHAUSTION_REVERSAL', 'FLAG', 'VWAP_RECLAIM'];
  if (!primarySetups.includes(setup.setup)) return setup;
  if (!candles.length) return setup;

  const currentIndex = candles.length - 1;
  const setupIndex = candles.findIndex((c) => c.t >= setup.detectedAt);
  const recentWindow = 30;
  const isOrbSetup = setup.setup === 'ORB';
  const isMomentumSetup = setup.setup === 'EXHAUSTION_REVERSAL';

  const eligibleCandidates = fvgCandidates.filter((fvg) => {
    if (!directionMatches(setup.direction, fvg)) return false;
    if (isFvgExpired(fvg, currentIndex)) return false;
    if (setupIndex >= 0) {
      if (isOrbSetup) {
        return fvg.originIndex >= setupIndex && fvg.originIndex <= setupIndex + 3;
      }
      if (isMomentumSetup) {
        return fvg.originIndex >= setupIndex;
      }
      if (fvg.originIndex >= setupIndex) return true;
    }
    return currentIndex - fvg.originIndex <= recentWindow;
  });

  if (!eligibleCandidates.length) return setup;

  const qualified = eligibleCandidates
    .map((fvg) => {
      const ctx = buildContextAtIndex(candles, indicators, trend, session, sessionStart, sessionEnd, fvg.originIndex);
      const result = qualifyFVG({ fvg, ctx, setup, candles, indicators, swings });
      return { ...result, ctx };
    })
    .filter((item) => item.eligible);

  if (!qualified.length) return setup;

  const currentPrice = candles[currentIndex].c;
  const sorted = [...qualified].sort((a, b) => {
    const strengthScore = (strength: FvgStrength) => (strength === 'strong' ? 2 : strength === 'valid' ? 1 : 0);
    const strengthDiff = strengthScore(b.fvg.strength) - strengthScore(a.fvg.strength);
    if (strengthDiff !== 0) return strengthDiff;
    const distA = Math.abs(a.fvg.mid - currentPrice);
    const distB = Math.abs(b.fvg.mid - currentPrice);
    return distA - distB;
  });

  const selected = sorted[0];
  const atr = indicators.atr14[currentIndex] ?? selected.ctx.atr;
  const buffer = computeBuffer(atr ?? null, currentPrice);
  const entryHint = selected.fvg.strength === 'strong' ? 'proximal' : 'mid';
  const entryLevel = entryHint === 'proximal'
    ? setup.direction === 'LONG'
      ? selected.fvg.low
      : selected.fvg.high
    : selected.fvg.mid;
  const stopLevel = setup.direction === 'LONG'
    ? selected.fvg.low - buffer
    : selected.fvg.high + buffer;

  const computeRR = (entry: number, stop: number, target: number) => {
    const risk = Math.abs(entry - stop);
    const reward = Math.abs(target - entry);
    return risk > 0 ? reward / risk : 0;
  };

  const buildRiskTargets = (entry: number, stop: number, direction: 'LONG' | 'SHORT') => {
    const risk = Math.max(Math.abs(entry - stop), Number.EPSILON);
    const multiplier = direction === 'LONG' ? 1 : -1;
    return [
      { level: entry + multiplier * risk * 2, label: 'Target 1 (2R)' },
      { level: entry + multiplier * risk * 3, label: 'Target 2 (3R)' }
    ];
  };

  const rBasedSetups = new Set(['EMA_PULLBACK', 'VWAP_RECLAIM', 'ORB', 'EXHAUSTION_REVERSAL', 'FLAG']);
  const updatedTargets = rBasedSetups.has(setup.setup)
    ? buildRiskTargets(entryLevel, stopLevel, setup.direction)
    : setup.targets;
  const updatedChecklist = setup.checklist
    ? setup.checklist.map((item) => {
        if (item.key !== 'rr') return item;
        const target = updatedTargets?.[0]?.level;
        if (target == null) return item;
        return { ...item, pass: computeRR(entryLevel, stopLevel, target) >= 2 };
      })
    : setup.checklist;
  const updatedDraw = setup.draw
    ? setup.draw.map((item) => {
        if (item.kind !== 'line') return item;
        if (item.id === 'target1' || (item.label ?? '').includes('Target 1')) {
          return { ...item, price: updatedTargets?.[0]?.level ?? item.price };
        }
        if (item.id === 'target2' || (item.label ?? '').includes('Target 2')) {
          return { ...item, price: updatedTargets?.[1]?.level ?? item.price };
        }
        return item;
      })
    : setup.draw;

  const scoreModifiers = [
    ...(setup.scoreModifiers || []),
    scoreModifierForStrength(selected.fvg.strength),
    ...selected.modifiers
  ];

  const qualifierText = selected.fvg.qualifiers.length
    ? `Qualifiers: ${selected.fvg.qualifiers.join(', ')}`
    : 'Qualifiers: none';

  const drawZoneId = `fvg-zone-${setup.id}`;
  const zoneEndIndex = Math.min(selected.fvg.originIndex + selected.fvg.expiresAfterBars, currentIndex);

  return {
    ...setup,
    entryOriginal: setup.entryOriginal ?? setup.entry,
    stopOriginal: setup.stopOriginal ?? setup.stop,
    entry: { ...setup.entry, level: entryLevel },
    stop: { ...setup.stop, level: stopLevel },
    targets: updatedTargets,
    checklist: updatedChecklist,
    execution: {
      model: 'FVG Refined',
      zone: [selected.fvg.low, selected.fvg.high],
      fvg: selected.fvg,
      entryHint
    },
    scoreModifiers,
    why: [
      ...setup.why,
      `Execution: FVG Refined (${selected.fvg.strength})`,
      `Zone: ${selected.fvg.low.toFixed(5)}–${selected.fvg.high.toFixed(5)}`,
      qualifierText
    ],
    draw: [
      ...(updatedDraw || []),
      {
        kind: 'zone',
        id: drawZoneId,
        startIndex: selected.fvg.originIndex,
        endIndex: zoneEndIndex,
        high: selected.fvg.high,
        low: selected.fvg.low,
        label: 'FVG'
      }
    ]
  };
};
